import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, AlertTriangle, Lightbulb, TrendingUp, CheckCircle2, Eye, Loader2 } from 'lucide-react';
import { HelpBubble } from '@/components/HelpBubble';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useCurrency } from '@/lib/useCurrency';

const TYPE_META = {
  investigate: { label: 'Investigate', icon: Search, tone: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800' },
  review: { label: 'Review', icon: AlertTriangle, tone: 'bg-rose-50 text-rose-900 border-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800' },
  opportunity: { label: 'Opportunity', icon: Lightbulb, tone: 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800' },
  emerging_trend: { label: 'Emerging Trend', icon: TrendingUp, tone: 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800' },
  monitor: { label: 'Monitor', icon: CheckCircle2, tone: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700' },
};

const STATUS_META = {
  new: { label: 'New', tone: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100' },
  acknowledged: { label: 'Acknowledged', tone: 'bg-blue-100 text-blue-800 dark:bg-blue-900/70 dark:text-blue-100' },
  investigating: { label: 'Investigating', tone: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/70 dark:text-indigo-100' },
  validated: { label: 'Validated', tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/70 dark:text-emerald-100' },
  dismissed: { label: 'Dismissed', tone: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

const OPEN_STATUSES = new Set(['new', 'acknowledged', 'investigating']);

function formatCurrency(v, cs) {
  if (v === null || v === undefined) return '—';
  if (v >= 1_000_000) return `${cs}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${cs}${(v / 1_000).toFixed(0)}K`;
  return `${cs}${Math.round(v).toLocaleString()}`;
}

function SignalCard({ signal, cs, onOpen, onInlineStatus }) {
  const meta = TYPE_META[signal.signal_type] || TYPE_META.investigate;
  const status = STATUS_META[signal.status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`signal-card-${signal.id}`}
      className={`border-l-4 rounded-r-lg border ${meta.tone} p-4 hover:shadow-md transition-shadow cursor-pointer`}
      onClick={() => onOpen(signal)}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider">{meta.label}</span>
            <Badge variant="outline" className="text-[10px] uppercase">{signal.severity}</Badge>
            <Select
              value={signal.status}
              onValueChange={(v) => onInlineStatus(signal, v)}
            >
              <SelectTrigger
                className={`h-5 w-auto px-1.5 py-0 gap-0.5 rounded-full border-0 text-[10px] uppercase font-medium shadow-none ${status.tone}`}
                data-testid={`signal-status-inline-${signal.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="validated">Validated</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <h3 className="font-semibold text-base leading-snug">{signal.title}</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{signal.question}</p>
          {signal.value_at_stake ? (
            <div className="text-xs mt-2 font-medium">
              Value at stake:&nbsp;
              <span className="font-semibold">{formatCurrency(signal.value_at_stake, cs)}</span>
            </div>
          ) : null}
          {signal.status_updated_by && signal.status !== 'new' ? (
            <div className="text-[10px] text-muted-foreground mt-1.5" data-testid={`signal-attribution-${signal.id}`}>
              {status.label} by {signal.status_updated_by}
              {signal.status_updated_at ? ` · ${new Date(signal.status_updated_at).toLocaleDateString()}` : ''}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SignalDetail({ signal, cs, onClose, onStatusUpdate, onNavigate }) {
  const [status, setStatus] = useState(signal?.status || 'new');
  const [note, setNote] = useState(signal?.status_note || '');
  const [realized, setRealized] = useState(signal?.realized_value ?? '');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setStatus(signal?._presetStatus || signal?.status || 'new');
    setNote(signal?.status_note || '');
    setRealized(signal?.realized_value ?? '');
  }, [signal]);

  if (!signal) return null;
  const meta = TYPE_META[signal.signal_type] || TYPE_META.investigate;

  const save = async () => {
    if (status === 'dismissed' && !note.trim()) {
      toast.error('Dismissing a signal requires a reason.');
      return;
    }
    setSaving(true);
    try {
      const payload = { status, note: note || null };
      if (status === 'validated' && realized !== '' && !Number.isNaN(Number(realized))) {
        payload.realized_value = Number(realized);
      }
      await api.post(`/signals/${signal.id}/status`, payload);
      toast.success('Status updated');
      onStatusUpdate?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!signal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="signal-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">{meta.label}</span>
            <Badge variant="outline" className="text-[10px] uppercase">{signal.severity}</Badge>
          </div>
          <DialogTitle className="text-xl">{signal.title}</DialogTitle>
          <DialogDescription className="text-base pt-1 leading-relaxed">
            {signal.question}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <section>
            <h4 className="text-sm font-semibold mb-1">Why it matters</h4>
            <p className="text-sm text-muted-foreground">{signal.why_matters}</p>
          </section>

          <section>
            <h4 className="text-sm font-semibold mb-1">What to investigate</h4>
            <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
              {(signal.what_to_investigate || []).map((it, i) => <li key={i}>{it}</li>)}
            </ul>
          </section>

          <section>
            <h4 className="text-sm font-semibold mb-1">Evidence</h4>
            <div className="grid grid-cols-2 gap-2 bg-muted/50 rounded-md p-3 text-xs">
              {Object.entries(signal.evidence || {}).map(([k, v]) => (
                <div key={k} className="col-span-1">
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">{k.replace(/_/g, ' ')}</div>
                  <div className="font-mono break-words">
                    {Array.isArray(v)
                      ? v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')
                      : typeof v === 'object' && v !== null
                        ? JSON.stringify(v)
                        : String(v)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {signal.value_at_stake ? (
            <div className="text-sm flex items-center gap-1">
              <span className="text-muted-foreground">Estimated value at stake: </span>
              <span className="font-semibold">{formatCurrency(signal.value_at_stake, cs)}</span>
              <HelpBubble text="Rough estimate of recoverable value if the investigation confirms the signal, based on spend scale and typical procurement benchmarks. Validate it before committing to a savings target." />
            </div>
          ) : null}

          <section className="border-t pt-4 space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-1">
              Investigation status
              <HelpBubble text="Lifecycle: New, Acknowledged, Investigating, then Validated (capture the realized value) or Dismissed (a reason is required). Status persists across data refreshes — a signal that stops triggering simply leaves the feed, its history stays on record. Tip: you can also change status inline from the badge on each card." />
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="signal-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="validated">Validated</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {status === 'validated' ? (
                <div>
                  <label className="text-xs text-muted-foreground">Realized value ({cs})</label>
                  <Input
                    type="number"
                    value={realized}
                    onChange={(e) => setRealized(e.target.value)}
                    placeholder="e.g. 42000"
                    data-testid="signal-realized-value"
                  />
                </div>
              ) : null}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                Note {status === 'dismissed' ? '(required)' : '(optional)'}
              </label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={status === 'dismissed' ? 'Reason for dismissing' : 'Findings or context'}
                data-testid="signal-note-input"
              />
            </div>
            {signal.status_updated_by && (
              <p className="text-[11px] text-muted-foreground" data-testid="signal-updated-by">
                Last updated by {signal.status_updated_by}
                {signal.status_updated_at ? ` on ${new Date(signal.status_updated_at).toLocaleString()}` : ''}
              </p>
            )}
          </section>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onNavigate?.(signal)}
            data-testid="signal-open-analytics"
          >
            <Eye className="w-4 h-4 mr-1" /> Open in analytics
          </Button>
          <Button onClick={save} disabled={saving} data-testid="signal-save-status">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SignalsPage({ embedded = false }) {
  const [signals, setSignals] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const { symbol: cs } = useCurrency();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/signals');
      setSignals(res.data.signals || []);
      setCounts(res.data.counts || {});
      setGated(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setGated(true);
      } else {
        toast.error('Failed to load signals');
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = signals;
    if (statusFilter === 'open') {
      out = out.filter((s) => OPEN_STATUSES.has(s.status));
    } else if (statusFilter === 'validated') {
      out = out.filter((s) => s.status === 'validated');
    } else if (statusFilter === 'dismissed') {
      out = out.filter((s) => s.status === 'dismissed');
    }
    if (typeFilter !== 'all') {
      out = out.filter((s) => s.signal_type === typeFilter);
    }
    return out;
  }, [signals, typeFilter, statusFilter]);

  const inlineStatus = async (signal, newStatus) => {
    if (newStatus === signal.status) return;
    if (newStatus === 'dismissed') {
      // Dismissal needs a reason — open the detail panel with it pre-selected
      setSelected({ ...signal, _presetStatus: 'dismissed' });
      return;
    }
    try {
      await api.post(`/signals/${signal.id}/status`, { status: newStatus });
      toast.success(`Marked as ${(STATUS_META[newStatus] || {}).label || newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    }
  };

  const navigateToPage = (signal) => {
    if (!signal.deep_link?.page) return;
    const params = new URLSearchParams(signal.deep_link.filters || {});
    const q = params.toString();
    navigate(`/${signal.deep_link.page}${q ? `?${q}` : ''}`);
  };

  if (gated) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="p-8 text-center">
            <Lightbulb className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <h2 className="text-xl font-semibold mb-2">Signal Intelligence</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              A prioritized feed of investigation-worthy signals — concentration shifts, price
              variance, contract leakage, duplicate suppliers and more — each with evidence and a
              "what to investigate" checklist.
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Ask your platform admin to enable <span className="font-medium">Signal Intelligence</span>
              &nbsp;in Platform Admin → Organizations → Subscribed Features (it's included by default on the Enterprise plan).
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-4'} data-testid="signals-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-1">
              Signal Intelligence
              <HelpBubble text="Seven deterministic detectors scan every transaction after each upload: supplier concentration shifts, unit-price variance, price-vs-volume decomposition, contract leakage, tail-spend fragmentation, duplicate suppliers, and classification drift. Results are ranked so the highest-value investigation surfaces first. Enterprise plan feature." />
            </h1>
            <p className="text-sm text-muted-foreground">
              Prioritized signals from your spend — what to investigate this week.
            </p>
          </div>
        )}
        <div className="flex gap-2 items-center ml-auto">
          <HelpBubble text="Investigate: anomalies worth digging into (concentration, price variance, leakage, duplicates, misclassification). Review: material period-over-period change decomposed into price vs volume vs mix. Opportunity: potential savings such as tail-spend consolidation. Emerging Trend: meaningful shifts in trajectory." />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="signal-status-filter">
              <SelectValue placeholder="Open" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open ({counts.open ?? 0})</SelectItem>
              <SelectItem value="all">All ({counts.total ?? 0})</SelectItem>
              <SelectItem value="validated">Completed ({counts.validated ?? 0})</SelectItem>
              <SelectItem value="dismissed">Shelved ({counts.dismissed ?? 0})</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="signal-type-filter">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types ({counts.total || 0})</SelectItem>
              <SelectItem value="investigate">Investigate ({counts.investigate || 0})</SelectItem>
              <SelectItem value="review">Review ({counts.review || 0})</SelectItem>
              <SelectItem value="opportunity">Opportunity ({counts.opportunity || 0})</SelectItem>
              <SelectItem value="emerging_trend">Emerging Trend ({counts.emerging_trend || 0})</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="signals-refresh">Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['investigate', 'review', 'opportunity', 'emerging_trend'].map((k) => {
          const meta = TYPE_META[k];
          const Icon = meta.icon;
          return (
            <Card key={k}>
              <CardContent className="p-4 flex items-center gap-3">
                <Icon className="w-6 h-6 text-muted-foreground" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{meta.label}</div>
                  <div className="text-2xl font-bold" data-testid={`count-${k}`}>{counts[k] || 0}</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Loading signals...</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">No signals match this filter</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {statusFilter === 'open'
              ? 'No open signals — everything flagged so far has been completed or shelved. Switch the filter to All to review them.'
              : 'Either your data is clean on these dimensions, or the underlying columns (quantity, contracted_spend, invoice_date) haven\'t been uploaded. Upload richer data to unlock more signals.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <SignalCard key={s.id} signal={s} cs={cs} onOpen={setSelected} onInlineStatus={inlineStatus} />
          ))}
        </div>
      )}

      <SignalDetail
        signal={selected}
        cs={cs}
        onClose={() => setSelected(null)}
        onStatusUpdate={load}
        onNavigate={navigateToPage}
      />
    </div>
  );
}
